import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { mockBlogPosts } from './BlogPage';
import './BlogDetailPage.css';

const BlogDetailPage = () => {
  const { name } = useParams();
  const navigate = useNavigate();
  const [blogContent, setBlogContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metaDescription, setMetaDescription] = useState(null);

  // Find the current blog post data
  const currentBlog = mockBlogPosts.find(post => post.slug === name);

  useEffect(() => {
    // Scroll to top when component mounts or name changes
    window.scrollTo(0, 0);
    
    const loadBlogContent = async () => {
      try {
        setLoading(true);
        setError(null);

        // Build HTML file URL
        const htmlUrl = `/local/blog/${name}.html`;

        // Fetch HTML content
        const response = await fetch(htmlUrl);

        if (!response.ok) {
          throw new Error(`Blog article not found: ${name}`);
        }

        const htmlContent = await response.text();
        setBlogContent(htmlContent);
        try {
          if (currentBlog.metaDescription && currentBlog.metaDescription !== '') {
            setMetaDescription(currentBlog.metaDescription);
          } else {
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlContent, 'text/html');
            const getText = (el) => (el && el.textContent ? el.textContent.trim() : undefined);
            const titleFromTag = getText(doc.querySelector('title'));
            const titleFromH1 = getText(doc.querySelector('.title h1, h1'));
            const finalTitle = titleFromTag || titleFromH1 || null;
            if (finalTitle) {
              setMetaDescription(`${finalTitle} - MemU Blog`);
            }
          }
        } catch {
          // ignore parsing error
        }
      } catch (err) {
        console.error('Failed to load blog content:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (name) {
      loadBlogContent();
    }
  }, [name, currentBlog]);

  const handleBackClick = () => {
    navigate('/blog');
  };

  if (loading) {
    return (
      <main>
        <div className="blog-detail-page-container">
          <div className="blog-detail-container">
            <div className="blog-detail-back-navigation">
              <button className="blog-detail-back-btn" onClick={handleBackClick}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                  <path d="M19 12H5M5 12L12 19M5 12L12 5" />
                </svg>
                Back to blog list
              </button>
            </div>

            <div className="blog-detail-loading">
              <div className="blog-detail-loading-spinner"></div>
              <p>Loading article content...</p>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main>
        <div className="blog-detail-page-container">
          <div className="blog-detail-container">
            <div className="blog-detail-back-navigation">
              <button className="blog-detail-back-btn" onClick={handleBackClick}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                  <path d="M19 12H5M5 12L12 19M5 12L12 5" />
                </svg>
                Back to blog list
              </button>
            </div>

            <div className="blog-detail-error">
              <div className="blog-detail-error-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
              </div>
              <h2>Failed to Load Article</h2>
              <p>{error}</p>
              <button className="blog-detail-retry-btn" onClick={() => window.location.reload()}>
                Retry
              </button>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <>
      <Helmet>
        <title>{currentBlog ? currentBlog.title : 'Blog Article - MemU'}</title>
        <meta name="description" content={currentBlog ? currentBlog.excerpt : 'Read the latest insights and stories from the MemU team about AI memory layer technology and best practices.'} />
        <meta name="keywords" content={currentBlog ? `${currentBlog.title.toLowerCase()}, memu blog, ai memory, ${currentBlog.category}` : 'memu blog, ai memory insights, llm memory guide'} />
        <meta name="author" content={currentBlog ? currentBlog.author : 'MemU Team'} />
        <meta name="article:published_time" content={currentBlog ? new Date(currentBlog.date).toISOString() : ''} />
        <meta name="article:author" content={currentBlog ? currentBlog.author : 'MemU Team'} />
        
        {/* Open Graph */}
        <meta property="og:title" content={currentBlog ? `${currentBlog.title} - MemU Blog` : 'Blog Article - MemU'} />
        <meta property="og:description" content={currentBlog ? currentBlog.excerpt : 'Read the latest insights and stories from the MemU team about AI memory layer technology and best practices.'} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://memu.pro/blog/${name}`} />
        <meta property="og:image" content={currentBlog ? currentBlog.image : 'https://d192tm8h0ep0ud.cloudfront.net/memu.pro/static/memu-logo.png'} />
        <meta property="og:article:published_time" content={currentBlog ? new Date(currentBlog.date).toISOString() : ''} />
        <meta property="og:article:author" content={currentBlog ? currentBlog.author : 'MemU Team'} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={currentBlog ? `${currentBlog.title} - MemU Blog` : 'Blog Article - MemU'} />
        <meta name="twitter:description" content={currentBlog ? currentBlog.excerpt : 'Read the latest insights and stories from the MemU team about AI memory layer technology and best practices.'} />
        <meta name="twitter:image" content={currentBlog ? currentBlog.image : 'https://d192tm8h0ep0ud.cloudfront.net/memu.pro/static/memu-logo.png'} />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": currentBlog ? currentBlog.title : 'MemU Blog Article',
            "description": metaDescription || (currentBlog ? currentBlog.excerpt : 'Insights from the MemU team'),
            "image": currentBlog ? currentBlog.image : 'https://d192tm8h0ep0ud.cloudfront.net/memu.pro/static/memu-logo.png',
            "datePublished": currentBlog ? new Date(currentBlog.date).toISOString() : '',
            "author": {
              "@type": "Person",
              "name": currentBlog ? currentBlog.author : 'MemU Team'
            },
            "publisher": {
              "@type": "Organization",
              "name": "MemU",
              "logo": {
                "@type": "ImageObject", 
                "url": "https://d192tm8h0ep0ud.cloudfront.net/memu.pro/static/logo.png"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://memu.pro/blog/${name}`
            },
            "url": `https://memu.pro/blog/${name}`
          })}
        </script>
      </Helmet>
      
      <main>
      <div className="blog-detail-page-container">
        <div className="blog-detail-container">
          <div className="blog-detail-back-navigation">
            <button className="blog-detail-back-btn" onClick={handleBackClick}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
                <path d="M19 12H5M5 12L12 19M5 12L12 5" />
              </svg>
              Back to blog list
            </button>
          </div>

          <div className="blog-detail-content">
            <div
              className="blog-detail-html-content"
              dangerouslySetInnerHTML={{ __html: blogContent }}
            />
          </div>
        </div>
      </div>
    </main>
    </>
  );
};

export default BlogDetailPage;
